import { Fragment, memo, useCallback, useState, useMemo } from "react"
import PropTypes from "prop-types"
import { Box, Tooltip, IconButton } from "@mui/material"
import { ArrowUpward, ArrowDownward } from "@mui/icons-material"
import { isFunction } from "lodash"
import classNames from "classnames"
import { TOOLTIP_MIN_HEADER_LENGTH } from "renderers/contributors-table/helpers"
import { sortDirLabel } from "helpers/sorts"
import CellTooltip from "components/securities-table/cell-tooltip"
import { getNextSortDirection } from "../helpers"
import { TOOLTIP_ENTER_DELAY_MS } from "helpers/constants"
import { CurrencyScaleProvider } from "helpers/metrics/currency-scale-label"
import GoToFinder, { NO_MATCH_MESSAGE } from "./go-to-finder"

// createLabelColumn sets colDef.headerName to the idLabel each view passes in ("Factor" for
// every factors-view template, "Ticker"/unset elsewhere) — reusing it here avoids threading a
// separate label down through create-label-column.jsx and every asset/factors template.
const FACTORS_NO_MATCH_MESSAGE = "No matching factors"

const DELAY_TAG_SX = { ml: 0.5, typography: "caption", color: "text.secondary" }

const SortButton = memo(({
  direction,
  isPreview = false,
  field,
  colDef,
  isAbsolute,
  onSortChange,
  getNextSortDirection: getNextSortDirectionProp,
}) => {
  const handleClick = useCallback((e) => {
    e.stopPropagation() // Prevent header click when clicking the button
    if (onSortChange && getNextSortDirectionProp) {
      const nextDirection = getNextSortDirectionProp()
      onSortChange(field, nextDirection)
    }
  }, [onSortChange, field, getNextSortDirectionProp])

  if (!direction) return null

  const ArrowIcon = direction === "asc" ? ArrowUpward : ArrowDownward

  return (
    <IconButton
      size="small"
      onClick={handleClick}
      className={classNames("MuiCustomHeader-sortIcon", {
        "MuiCustomHeader-sortIconAbsolute": isAbsolute && !isPreview,
        "MuiCustomHeader-sortIconPreview": isPreview,
      })}
      aria-label={`Sort ${colDef.headerName} ${direction === "asc" ? "ascending" : "descending"}`}
    >
      <ArrowIcon fontSize="inherit" />
    </IconButton>
  )
})

SortButton.displayName = "SortButton"

SortButton.propTypes = {
  direction: PropTypes.string,
  isPreview: PropTypes.bool,
  field: PropTypes.string.isRequired,
  colDef: PropTypes.shape({
    headerName: PropTypes.string.isRequired,
  }).isRequired,
  isAbsolute: PropTypes.bool,
  onSortChange: PropTypes.func,
  getNextSortDirection: PropTypes.func,
}

/**
 * HeaderTextButton - A button component that looks like plain text but supports
 * click interactions without interfering with MUI DataGrid column dragging.
 *
 * This component solves the issue where text elements with click handlers
 * prevent column drag-and-drop functionality in MUI DataGrid.
 *
 */
const HeaderTextButton = memo(({ children, onClick, disabled = false, className = "" }) => {
  return (
    <button
      className={classNames("MuiCustomHeader-text", className)}
      onClick={onClick}
      disabled={disabled}
      type="button"
    >
      {children}
    </button>
  )
})

HeaderTextButton.displayName = "HeaderTextButton"

HeaderTextButton.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
}

const TableHeader = memo(({
  field,
  colDef,
  sortMetricId,
  isAbsoluteSort,
  sortArrowDirection,
  metricMetadata,
  onSortChange,
  data,
  goToRows,
  onGoToRow,
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const [headerCellNode, setHeaderCellNode] = useState(null)

  const showGoToFinder = Boolean(goToRows && onGoToRow)

  // renderHeader only reaches the title-container content, not MuiDataGrid-columnHeader itself.
  const rootRef = useCallback((node) => {
    if (node) setHeaderCellNode(node.closest(".MuiDataGrid-columnHeader"))
  }, [])

  const isSortedColumn = sortMetricId === field
  const isAbsolute = isSortedColumn && isAbsoluteSort
  const metric = metricMetadata?.find(m => m.id === field)
  const isTextColumn = colDef.type === "string"

  const tooltipContentsValue = useMemo(() => {
    if (!metric?.tooltipContents) return null

    if (isFunction(metric.tooltipContents)) {
      return metric.tooltipContents(data || {})
    }
    return metric.tooltipContents
  }, [metric, data])

  const getNextSortDirectionForColumn = useCallback(() => {
    return getNextSortDirection({
      isSortedColumn,
      isTextColumn,
      isAbsolute,
      sortArrowDirection,
    })
  }, [isSortedColumn, isTextColumn, isAbsolute, sortArrowDirection])

  const handleHeaderClick = useCallback(() => {
    if (!onSortChange) return

    const nextDirection = getNextSortDirectionForColumn()
    onSortChange(field, nextDirection)
  }, [onSortChange, field, getNextSortDirectionForColumn])

  const getPreviewArrowDirection = useCallback(() => {
    if (isSortedColumn) return null
    const nextDirection = getNextSortDirectionForColumn()

    // If next direction is null (no sort), don't show preview arrow
    if (nextDirection === null) return null

    return nextDirection.includes("asc") ? "asc" : "desc"
  }, [isSortedColumn, getNextSortDirectionForColumn])

  const showTooltip = metric?.shouldShowTooltip?.(colDef.headerName) ?? colDef.headerName.length > TOOLTIP_MIN_HEADER_LENGTH
  const tooltipText = metric?.getTooltipText?.(colDef.headerName) ?? colDef.headerName

  const sortDescription = useMemo(() => {
    if (!isSortedColumn) return null
    const alphabetical = colDef.type === "string"
    const dir = sortArrowDirection
    const abs = isAbsolute
    return sortDirLabel({ dir, abs, alphabetical })
  }, [isSortedColumn, isAbsolute, sortArrowDirection, colDef.type])

  const shouldShowTooltip = showTooltip || !!sortDescription
  const tooltipTitle = (
    <CurrencyScaleProvider value={colDef.currencyScale ?? null}>
      {tooltipText}
      {sortDescription && <div>{sortDescription}</div>}
    </CurrencyScaleProvider>
  )

  const HeaderContent = (
    <Box
      ref={rootRef}
      data-field={field}
      className={classNames("MuiCustomHeader-root", {
        "MuiCustomHeader-alignLeft": isTextColumn,
        "MuiCustomHeader-alignRight": !isTextColumn,
        "MuiCustomHeader-active": isSortedColumn,
        "MuiCustomHeader-clickable": onSortChange,
      })}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box className="MuiCustomHeader-content">
        <HeaderTextButton
          onClick={handleHeaderClick}
          disabled={!onSortChange}
        >
          <span className="MuiCustomHeader-text-wrapper">
            <span data-test-class="table-header-label-text">{colDef.headerName}</span>
            {metric?.delayTag && (
              <Box component="span" sx={DELAY_TAG_SX}>{metric.delayTag}</Box>
            )}
            {tooltipContentsValue && (
              <CellTooltip contents={tooltipContentsValue} />
            )}
          </span>
        </HeaderTextButton>

        {isSortedColumn && (
          <SortButton
            direction={sortArrowDirection}
            field={field}
            colDef={colDef}
            isAbsolute={isAbsolute}
            onSortChange={onSortChange}
            getNextSortDirection={getNextSortDirectionForColumn}
          />
        )}

        {!isSortedColumn && isHovered && onSortChange && (
          <SortButton
            direction={getPreviewArrowDirection()}
            isPreview={true}
            field={field}
            colDef={colDef}
            isAbsolute={isAbsolute}
            onSortChange={onSortChange}
            getNextSortDirection={getNextSortDirectionForColumn}
          />
        )}
      </Box>
    </Box>
  )

  return (
    <Fragment>
      {shouldShowTooltip ? (
        <Tooltip title={tooltipTitle} placement="top" enterDelay={TOOLTIP_ENTER_DELAY_MS}>
          {HeaderContent}
        </Tooltip>
      ) : (
        HeaderContent
      )}
      {showGoToFinder && (
        <GoToFinder
          rows={goToRows}
          onGoToRow={onGoToRow}
          headerCellNode={headerCellNode}
          noMatchMessage={colDef.headerName === "Factor" ? FACTORS_NO_MATCH_MESSAGE : NO_MATCH_MESSAGE}
        />
      )}
    </Fragment>
  )
})

TableHeader.displayName = "TableHeader"

TableHeader.propTypes = {
  field: PropTypes.string.isRequired,
  colDef: PropTypes.shape({
    headerName: PropTypes.string.isRequired,
    type: PropTypes.string,
    currencyScale: PropTypes.string,
  }).isRequired,
  sortMetricId: PropTypes.string,
  isAbsoluteSort: PropTypes.bool,
  sortArrowDirection: PropTypes.string,
  metricMetadata: PropTypes.array,
  onSortChange: PropTypes.func,
  data: PropTypes.object,
  goToRows: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    children: PropTypes.object,
  })),
  onGoToRow: PropTypes.func,
}

export default TableHeader
